import {getRequest, postRequest, putRequest, toQueryString} from './request';
import {FirstRound, GameConfig, IDailyProgress} from '../common/types/game';
import {AlbumsResponse, UserPupStarCards} from '../common/types/albums';
import {AxiosRequestConfig} from 'axios';

function withTopicIdParams(
  config: AxiosRequestConfig | undefined,
  topicId: number | undefined
): AxiosRequestConfig | undefined {
  if (topicId == null || !Number.isFinite(topicId)) {
    return config;
  }
  return {
    ...config,
    params: { ...config?.params, topicId },
  };
}

export const getCurrentGame = async (
  config?: AxiosRequestConfig,
  topicId?: number
): Promise<GameConfig> => {
  const merged = withTopicIdParams(config, topicId);
  const response = await getRequest<GameConfig | {code: number}>(`games/ongoing-game`, undefined, merged);
  if ('code' in response.data) {
    const error: any = new Error(`Error fetching current game: ${response.data.code}`);
    error.response = response;
    throw error;
  }

  return response.data as GameConfig;
};

/** Abandon the current ongoing game (e.g. user closed from RN WebView). */
export const abandonOngoingGame = async (
  config?: AxiosRequestConfig,
  topicId?: number
): Promise<unknown> => {
  const merged = withTopicIdParams(config, topicId);
  const res = await postRequest<unknown>(`games/ongoing-game/abandon`, {}, merged);
  return res.data;
};

export const getAlbums = async (config?: AxiosRequestConfig): Promise<AlbumsResponse> => {
  return await getRequest<any>(`albums`, undefined, config).then((res) => res.data);
};

export const getGameConfig = async (
  config?: AxiosRequestConfig,
  isNewSeq?: boolean,
  topicId?: number
): Promise<GameConfig> => {
  const query: Record<string, string | number | boolean> = {};
  if (typeof isNewSeq === "boolean") {
    query.isNewSeq = isNewSeq;
  }
  if (topicId != null && Number.isFinite(topicId)) {
    query.topicId = topicId;
  }
  let url = "games/game-config";
  if (Object.keys(query).length) {
    url += "?" + toQueryString(query);
  }
  return await getRequest<GameConfig>(url, undefined, config).then(
    (res) => res.data,
  );
};

export const startGame = async (gameId: number, config?: AxiosRequestConfig): Promise<any> => {
  return await postRequest<any>(`user-games/games/${gameId}`, null, config).then(
    (res) => res.data,
  );
};

export const setAnswer = async (gameId: number, answerId: number, secondAnswerId?: string,  config?: AxiosRequestConfig): Promise<any> => {
  return await putRequest<any>(`user-games/games/${gameId}/answers/${answerId}`, {secondAnswerId}, config).then(
      (res) => res.data,
  );
};

export const setHint = async (gameId: number, config?: AxiosRequestConfig): Promise<any> => {
  return await putRequest<any>(`user-games/games/${gameId}/hint`, {}, config).then(
      (res) => res.data,
  );
};

export const setPupStarCardsSeen = async (cards: UserPupStarCards[]): Promise<any> => {
  return Promise.all(cards.map((card) => setPupStarCardSeen(card.pupStarCardId)))
    .catch((err) => console.error(err));
};


export const setPupStarCardSeen = async (id: number): Promise<any> => {
  return await putRequest<any>(`user-pup-star-cards/${id}/seen`, {}).then(
    (res) => res.data,
  );
};

export const setPupStarCardsCollected = async (): Promise<boolean> => {
  const res = await putRequest<boolean>(`user-pup-star-cards/collected` , {});
  return res.data;
};


export const getFirstRound = async (config?: AxiosRequestConfig): Promise<FirstRound> => {
  return await getRequest<any>(`games/daily/first-round`, undefined, config).then((res) => res.data);
};

export const getDailyProgress = async (config?: AxiosRequestConfig): Promise<IDailyProgress> => {
  return await getRequest<IDailyProgress>(`user-games/daily-progress`, undefined, config).then((res) => res.data);
};

export const setAllDone = async (config?: AxiosRequestConfig): Promise<any> => {
  return await putRequest<any>(`user-games/done`, {}, config).then(
      (res) => res.data,
  );
};
